import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtNum, curSym } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { exportToCsv } from "@finance/lib/exportCsv";
import { Button } from "@finance/components/ui/button";
import { DownloadSimple, TrendDown, Clock, Scales } from "@phosphor-icons/react";
import { KpiStrip, KpiCard, EmptyState } from "@finance/components/DesignSystem";

export default function GpVariance() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { api.get("/reports/gp-variance").then((r) => setData(r.data)).catch(() => setErr(true)); }, []);

  const rows = data?.rows || [];
  const cols = [
    { key: "lx_po_no", label: "Somcore PO" }, { key: "client_name", label: "Client" },
    { key: "supplier_name", label: "Supplier" }, { key: "currency", label: "Ccy" },
    { key: "revenue", label: "Revenue", format: fmtNum }, { key: "cogs", label: "Cost of Sales", format: fmtNum },
    { key: "gp", label: "Gross Profit", format: fmtNum }, { key: "margin_pct", label: "Margin %" },
    { key: "po_cost_variance", label: "PO vs Actual Variance", format: fmtNum },
    { key: "gp_estimated", label: "Awaiting Supplier Invoice", format: (v) => (v ? "Yes" : "No") },
  ];

  return (
    <Layout>
      <PageHeader
        title="GP Variance"
        subtitle="POs whose margin is still on a PO-cost estimate (supplier invoice not matched) or whose actual supplier cost diverged from the PO estimate."
        actions={<Button variant="outline" data-testid="export-gp-variance" onClick={() => exportToCsv("gp-variance", rows, cols)}><DownloadSimple size={16} className="mr-1" />Export</Button>}
      />
      <div className="p-8">
        {err ? (
          <div className="rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="gpv-error">Could not load the GP Variance report. Please refresh, or check that you have executive access.</div>
        ) : !data ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : (
          <>
            <KpiStrip cols={3}>
              <KpiCard icon={Scales} label="POs Flagged" value={data.summary.count} tone={data.summary.count ? "danger" : "info"} testid="kpi-gpv-count" />
              <KpiCard icon={Clock} label="Awaiting Supplier Invoice" value={data.summary.awaiting_supplier_invoice} tone="accent" testid="kpi-gpv-awaiting" />
              <KpiCard icon={TrendDown} label="Net PO→Actual Variance (TZS)" value={fmtNum(data.summary.total_variance, "TZS")} tone="accent" testid="kpi-gpv-variance" />
            </KpiStrip>

            {rows.length === 0 ? (
              <EmptyState icon={Scales} testid="gpv-empty" title="No margin variances" message="Every invoiced PO has its supplier cost reconciled, and no actual cost has diverged from the PO estimate." />
            ) : (
              <div className="overflow-x-auto rounded-sm border border-gray-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
                    <th className="px-4 py-2 text-left">Somcore PO</th><th className="px-4 py-2 text-left">Client</th>
                    <th className="px-4 py-2 text-left">Supplier</th>
                    <th className="px-4 py-2 text-right">Revenue</th><th className="px-4 py-2 text-right">Cost of Sales</th>
                    <th className="px-4 py-2 text-right">GP</th><th className="px-4 py-2 text-right">Margin</th>
                    <th className="px-4 py-2 text-right">PO→Actual Variance</th><th className="px-4 py-2 text-left">Cost Basis</th>
                    <th className="px-4 py-2 text-right">·</th>
                  </tr></thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.transaction_id} data-testid={`gpv-row-${r.transaction_id}`} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2 font-mono-num font-medium">{r.lx_po_no || "—"}</td>
                        <td className="px-4 py-2">{r.client_name || "—"}</td>
                        <td className="px-4 py-2">{r.supplier_name || "—"}</td>
                        <td className="px-4 py-2 text-right lx-num">{curSym(r.currency)} {fmtNum(r.revenue)}</td>
                        <td className="px-4 py-2 text-right lx-num">{curSym(r.currency)} {fmtNum(r.cogs)}</td>
                        <td className={`px-4 py-2 text-right lx-num font-semibold ${r.gp >= 0 ? "text-emerald-700" : "text-red-600"}`}>{curSym(r.currency)} {fmtNum(r.gp)}</td>
                        <td className="px-4 py-2 text-right lx-num">{r.margin_pct == null ? "—" : `${r.margin_pct}%`}</td>
                        <td className={`px-4 py-2 text-right lx-num font-semibold ${Math.abs(r.po_cost_variance) > 0.5 ? (r.po_cost_variance > 0 ? "text-red-600" : "text-emerald-700") : "text-gray-400"}`}>{r.po_cost_variance ? fmtNum(r.po_cost_variance, "TZS") : "—"}</td>
                        <td className="px-4 py-2">
                          {r.gp_estimated
                            ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">PO estimate</span>
                            : <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Reconciled</span>}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button className="text-xs font-semibold text-[#1B4383] hover:underline" data-testid={`gpv-open-${r.transaction_id}`} onClick={() => navigate(`/transactions/${r.transaction_id}`)}>Open PO</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-4 text-xs text-gray-400">A positive PO→Actual variance means the supplier billed more than the PO estimate (margin reduced); negative means they billed less (margin improved). "PO estimate" rows are awaiting a supplier invoice to reconcile.</p>
          </>
        )}
      </div>
    </Layout>
  );
}
